import { useCallback, useState, useContext } from "react";
import { AppContext } from "contexts";
import { mapObject, mapObjects } from "services";
import { useRouter } from "hooks";
import { Path } from "paths";
// import { environment } from "environments/environment";

const useApi = ({
  api,
  params = {},
  handleOwnError = false,
  handleVenueBadRequest = false,
  mapper,
  isArray = false,
  paramsMapper,
  returnMappedData = false,
  pageError = false,
}) => {
  const { history } = useRouter();
  const [loading, setLoading] = useState(undefined);
  const [error, setError] = useState(false);
  const [result, setResult] = useState(undefined);
  const [mappedData, setMappedData] = useState(isArray ? [] : {});
  const { globalErrorModal, setGlobalError } = useContext(AppContext);
  const [submittedParams, setSubmittedParams] = useState({});

  const mapData = useCallback(
    (res, params) => {
      let obj = null;
      if (isArray) {
        obj = mapObjects([...res.data], mapper, { ...params });
      } else {
        obj = mapObject({ ...res.data }, mapper, { ...params });
      }
      setMappedData(obj);
      return obj;
    },
    [mapper, isArray]
  );

  const request = useCallback(
    async (p) => {
      setLoading(true);
      setError(false);
      setGlobalError(false);
      try {
        setSubmittedParams({ ...params, ...p });
        const parameter = paramsMapper
          ? mapObject({ ...params, ...p }, paramsMapper)
          : { ...params, ...p };
        const res = await api(parameter);
        setResult(res);
        setLoading(false);
        if (res) {
          const mappedDataResponse = mapData(res, parameter);
          if (returnMappedData) {
            return mappedDataResponse;
          }
        }
        return res;
      } catch (err) {
        console.error("API ERROR", err);
        const metadata = err?.metadata;
        const code = metadata?.code;
        err.code = code;

        setError(true);
        setLoading(false);

        const getErrorResponse = (type, defaultError) => {
          if (
            (handleOwnError && handleOwnError[type]) ||
            (typeof handleOwnError === "boolean" && handleOwnError)
          ) {
            if (handleOwnError[type] && typeof handleOwnError[type] === "function") {
              handleOwnError[type]();
            } else {
              throw err;
            }
          } else {
            defaultError();
          }
        };

        const showErrorAppState = () => {
          globalErrorModal.show();
        };

        err.handleError = () => {
          showErrorAppState();
        };

        if (pageError) {
          setGlobalError(true);
          throw err;
        }

        const obj = {
          gateway: () => {
            setGlobalError(true);
            throw err;
          },
          network: () => {
            getErrorResponse("network", () => showErrorAppState());
            throw err;
          },
          unauthorized: () => {
            if (code === "1003") {
              // const param = localStorage.getItem("venueId");
              // window.location.href = `${environment.POUCH_ID}auth?type=VENUE&redirectUrl=${
              //   window.location.pathname
              // }&param=${param || ""}`;
              localStorage.removeItem("accessToken");
              throw err;
            }
            if (handleOwnError?.unauthorized) {
              const handler = handleOwnError.unauthorized;

              if (code && typeof handler === "object" && handler.hasOwnProperty(code)) {
                handler[code]();
              } else {
                showErrorAppState();
              }
            } else {
              showErrorAppState();
            }
            throw err;
          },
          server: () => {
            getErrorResponse("server", () => showErrorAppState());
            throw err;
          },
          badrequest: () => {
            if (code === "3025" && !handleVenueBadRequest) {
              // if request has no access in venueId
              localStorage.clear();
              globalErrorModal.show();
              history.push(Path.VENUE_SELECT);
            } else if (handleOwnError?.badrequest) {
              const handler = handleOwnError.badrequest;
              if (code && typeof handler === "object" && handler.hasOwnProperty(code)) {
                handler[code]();
              }
            } else {
              getErrorResponse("badrequest", () => showErrorAppState());
            }
            throw err;
          },
        };

        if (obj.hasOwnProperty(err.error)) {
          obj[err.error]();
        } else {
          showErrorAppState();
          throw err;
        }
      }
    },
    [
      params,
      api,
      handleOwnError,
      paramsMapper,
      globalErrorModal,
      history,
      mapData,
      returnMappedData,
      setGlobalError,
      pageError,
      handleVenueBadRequest,
    ]
  );

  return { request, loading, result, error, mappedData, submittedParams };
};

export default useApi;
